import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { TID } from "@/lib/testIds";
import { EASE } from "@/lib/motion";
import { NAV_COLUMNS, CTA } from "@/lib/siteData";
import { scrollToElement } from "@/lib/smoothScroll";
import { HEADER_HEIGHT } from "./SiteLayout";

// The design (30:262) lays the four columns on a 1440 grid at x=256/565/874/1183
// with the CTA block centred below them. Rendered here as a fluid 4-up grid so it
// collapses on narrow viewports instead of holding the absolute positions.
export const MenuOverlay = ({ isOpen, onClose }) => {
    // Lock the page behind the overlay while it is open.
    useEffect(() => {
        document.documentElement.style.overflow = isOpen ? "hidden" : "";
        return () => {
            document.documentElement.style.overflow = "";
        };
    }, [isOpen]);

    // Escape closes the menu — expected of any full-screen overlay.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isOpen, onClose]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ y: "-100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "-100%" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="fixed inset-0 z-[80] overflow-y-auto bg-paper pb-16 pt-[140px]"
                >
                    <nav
                        aria-label="Menu principal"
                        className="mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-6 sm:grid-cols-2 md:px-12 lg:grid-cols-4"
                    >
                        {NAV_COLUMNS.map((column) => (
                            <div key={column.title} className="flex flex-col items-center gap-6">
                                <h2 className="text-heading font-bold text-ink">
                                    {column.title}
                                </h2>
                                <ul className="flex flex-col items-center gap-[15px]">
                                    {column.links.map((link) => (
                                        <li key={link.label} className="text-center">
                                            <MenuLink link={link} onNavigate={onClose} />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>

                    <div className="mx-auto mt-20 flex max-w-[1440px] flex-col items-center px-6 text-center md:px-12">
                        <p className="type-display text-display text-brand">
                            {CTA.menu.headline}
                        </p>
                        <p className="mt-3 text-body text-ink">
                            {CTA.menu.subhead[0]}
                            <strong className="font-bold">{CTA.menu.subhead[1]}</strong>
                        </p>
                        <Link
                            to="/contato"
                            onClick={onClose}
                            data-testid={TID.navbarCta}
                            className="type-display mt-8 inline-flex h-[50px] w-[269px] items-center justify-center bg-brand text-action text-paper btn-sweep [--sweep:theme(colors.brand.deep)]"
                        >
                            {CTA.menu.action}
                        </Link>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// A column entry is either an in-app route or an external link. Labels carrying a
// newline are broken as drawn (e.g. "O QUE NOSSOS / CLIENTES DIZEM" at 30:295).
const MenuLink = ({ link, onNavigate }) => {
    const location = useLocation();

    const label = link.label.split("\n").map((line) => (
        <span key={line} className="block">
            {line}
        </span>
    ));

    const className =
        "text-item font-medium text-ink transition-colors duration-300 hover:text-brand link-sweep";

    if (link.external) {
        return (
            <a
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className={className}
            >
                {label}
            </a>
        );
    }

    const handleClick = (e) => {
        onNavigate();

        if (link.to?.includes("#")) {
            const [targetPath, hash] = link.to.split("#");
            const isSamePage = location.pathname === (targetPath || "/");

            // Unlock document scroll immediately so smooth scroll can proceed
            document.documentElement.style.overflow = "";

            if (isSamePage) {
                e.preventDefault();
                if (window.location.hash !== `#${hash}`) {
                    window.history.pushState(null, "", `#${hash}`);
                }
                const target = document.getElementById(hash);
                if (target) {
                    scrollToElement(target, { offset: HEADER_HEIGHT, duration: 1.2 });
                }
            }
        }
    };

    return (
        <Link to={link.to} onClick={handleClick} className={className}>
            {label}
        </Link>
    );
};
